import { useEffect } from "react";

import { trpc } from "@blis/trpc/react";

import { SearchableSelect } from "@/components/ui/searchable-select";
import { Text } from "@/components/ui/text";
import { useFormPrimaryOrg } from "../../form/common";

export function PrimaryClientSelect({
  value,
  onChange,
  inputRef,
}: {
  value: number;
  onChange: (value: number) => void;
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  const { allowedOrgs } = useFormPrimaryOrg();
  const primaryOrgs = trpc.orgs.primaryOrgsList.useQuery();

  const options = primaryOrgs.data || [];

  const orgOptions =
    allowedOrgs === "all"
      ? options
      : options.filter((opt) => allowedOrgs?.includes(opt.id));
  const selectedKeys =
    Number.isNaN(value) || value === null
      ? new Set<number>()
      : new Set([value]);

  // Auto-select if only one option is available
  useEffect(() => {
    if (orgOptions.length === 1 && (Number.isNaN(value) || value === null)) {
      onChange(orgOptions[0].id);
    }
  }, [orgOptions, value, onChange]);

  return (
    <SearchableSelect
      selectionMode="single"
      selectedKeys={selectedKeys}
      onSelectionChange={(keys) => {
        if (keys === "all") return;
        const first = keys.values().next().value;
        if (keys.size === 0) {
          onChange(Number.NaN);
        } else if (typeof first === "number") {
          onChange(first);
        }
      }}
      renderEmptyState={() => (
        <Text muted size="1" className="p-2">
          No clients found
        </Text>
      )}
    >
      <SearchableSelect.Button className="justify-between">
        <SearchableSelect.Value
          items={orgOptions}
          getKey={(item) => item.id}
          getText={(item) => item.name}
          placeholder="Select..."
          className="flex-1 text-start"
        />
      </SearchableSelect.Button>
      <SearchableSelect.Popover placement="bottom end">
        <SearchableSelect.Input
          ref={inputRef}
          placeholder="Search clients..."
        />
        <SearchableSelect.List items={orgOptions} shouldFocusOnHover>
          {(item) => (
            <SearchableSelect.Item id={item.id} textValue={item.name}>
              {item.name}
            </SearchableSelect.Item>
          )}
        </SearchableSelect.List>
      </SearchableSelect.Popover>
    </SearchableSelect>
  );
}
